import { Link, Navigate } from '@tanstack/react-router';
import { THRESHOLD_RANGES, type StravaStatus } from '@painpal/contracts';
import { Lightbulb } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { invalidateTraining } from '../../api/keys';
import { useCreateThreshold } from '../../api/mutations';
import { useStravaStatus, useThresholds } from '../../api/queries';
import { errorMessage } from '../../lib/api';
import { isImporting } from '../../lib/strava';
import { useOnTransition } from '../../ui/useOnTransition';
import { DEFAULT_FTP } from '../settings/thresholdModel';
import { OnboardingFrame } from './OnboardingFrame';
import styles from './onboarding.module.css';

export const FTP_STEP = 5;

export const clampFtp = (value: number): number => Math.min(THRESHOLD_RANGES.ftp.max, Math.max(THRESHOLD_RANGES.ftp.min, Math.round(value)));
export const numberOrZero = (value: number): number => (Number.isNaN(value) ? 0 : value);

// Strava's FTP, once a sync has read it, is a better first guess than the default.
export function startingFtp(strava: StravaStatus | undefined): number {
  const ftp = strava?.ftp ?? null;
  return ftp === null ? DEFAULT_FTP : clampFtp(ftp);
}

// One element for every render: Navigate navigates again whenever its props object changes, and a fresh element has fresh props.
const SKIP_TO_RIDES = <Navigate to="/onboarding/rides" replace />;

function FtpStepper({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <div className={styles.numberRow}>
      <button type="button" className={styles.stepButton} aria-label="Decrease FTP by 5 W" onClick={() => onChange(clampFtp(value - FTP_STEP))}>−</button>
      <span className={styles.value}>
        <input
          className={styles.number} type="number" inputMode="numeric" aria-label="FTP in watts"
          style={{ width: `${String(value).length}ch` }} value={value} onChange={(e) => onChange(numberOrZero(e.currentTarget.valueAsNumber))}
        />
        <span className={styles.unit}>W</span>
      </span>
      <button type="button" className={styles.stepButton} aria-label="Increase FTP by 5 W" onClick={() => onChange(clampFtp(value + FTP_STEP))}>+</button>
    </div>
  );
}

function Pitch() {
  return (
    <section className={styles.pitch}>
      <p className={styles.eyebrow}>STEP 01 / 02</p>
      <h1 className={styles.ftpTitle}><span>WHAT&apos;S YOUR</span> <span className={styles.ftpTitleBig}>FTP?</span></h1>
      <p className={styles.bodyCopy}>
        Your FTP is the power you can hold for about an hour. PainPal uses it to turn every ride into a Load score, so one
        hour at FTP = 100. Change it later and we&apos;ll recalculate everything from that date.
      </p>
    </section>
  );
}

export function FtpStepPage({ today }: { today: string }) {
  const queryClient = useQueryClient();
  const create = useCreateThreshold('ftp');
  const thresholds = useThresholds('ftp');
  const strava = useStravaStatus();
  const [typed, setTyped] = useState<number | null>(null);
  const value = typed ?? startingFtp(strava.data);
  const [date, setDate] = useState(today);
  const [leftToAutoApply, setLeftToAutoApply] = useState(false);
  // An import whose last ride lands while the rider is here may have auto-applied an FTP.
  useOnTransition(isImporting(strava.data?.backfill), (wasImporting) => { if (wasImporting) void invalidateTraining(queryClient); });

  function save(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // Once the import's rides have landed, the auto-apply enters Strava's FTP from the earliest one. Saving the same
    // value from today would take its place and leave every earlier ride without a Load, so it is left to that.
    const stravaUntouched = typed === null && date === today && value === strava.data?.ftp;
    if (stravaUntouched && isImporting(strava.data?.backfill)) setLeftToAutoApply(true);
    else create.mutate({ kind: 'ftp', value: clampFtp(value), effectiveFrom: date });
  }

  if (thresholds.isPending) return null;
  // A saved entry is the same fact as an existing one, so the save and the skip share one navigation.
  if (create.isSuccess || leftToAutoApply || (thresholds.data?.items.length ?? 0) > 0) return SKIP_TO_RIDES;

  return (
    <OnboardingFrame step={1} skip={<Link to="/onboarding/rides" className={styles.skip}>SKIP FOR NOW →</Link>}>
      <Pitch />
      <form className={styles.ftpCard} onSubmit={save} aria-label="Set your FTP">
        <span className={styles.cardLabel}>FUNCTIONAL THRESHOLD POWER</span>
        <FtpStepper value={value} onChange={setTyped} />
        <label className={styles.field}>
          <span className={styles.label}>VALID FROM</span>
          <input className={styles.input} type="date" value={date} max={today} required onChange={(e) => setDate(e.currentTarget.value)} />
        </label>
        <p className={styles.tip}>
          <Lightbulb size={16} aria-hidden="true" />
          Not sure? Take 95% of your best 20-minute power. A rough number now beats none: you can refine it any time.
        </p>
        {create.isError && <p role="alert" className="alert-hot">{errorMessage(create.error)}</p>}
        <div className={styles.actionBar} data-part="action-bar">
          <button type="submit" className="btn btn-ink btn-block" disabled={create.isPending}>SAVE &amp; CONTINUE →</button>
        </div>
      </form>
    </OnboardingFrame>
  );
}
